/**
 * Referral link utilities
 *
 * Flow:
 *  1. Visitor lands on builderslab.net?ref=USERCODE
 *  2. captureReferralCode() saves the code to localStorage
 *  3. After sign-up, AuthContext calls applyStoredReferral()
 *  4. applyStoredReferral() hits POST /api/referral/apply and clears localStorage
 */

const STORAGE_KEY = 'bl_ref';
const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Read ?ref= from the current URL and save it to localStorage.
 * Call once on app mount (e.g. inside main.jsx or App.jsx).
 */
export function captureReferralCode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (code && code.trim()) {
      // Kept as written ("John01"). The server matches any capitalisation.
      localStorage.setItem(STORAGE_KEY, code.trim());
    }
  } catch {
    // localStorage may be unavailable in certain private-browsing modes
  }
}

/**
 * If a referral code was stored, apply it via the API and remove it.
 * Safe to call multiple times — the API is idempotent (409 on re-use).
 *
 * @param {string} accessToken — Supabase session access token
 */
export async function applyStoredReferral(accessToken) {
  let code;
  try {
    code = localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }

  if (!code) return;

  try {
    const res = await fetch(`${API_URL}/api/referral/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ code }),
    });

    const data = await res.json();

    if (res.ok) {
      console.log(`[REFERRAL] Applied code "${code}": +${data.creditsAwarded} credits`);
    } else if (res.status === 409) {
      // Already used — silently clear and move on
      console.log('[REFERRAL] Code already applied, clearing.');
    } else {
      console.warn('[REFERRAL] Apply failed:', data.error);
      return; // Keep the code in storage so we can retry
    }

    // Clear after success or permanent failure (409)
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('[REFERRAL] Network error during apply:', err.message);
    // Keep in storage for retry on next session
  }
}
